import { createElement, Fragment, useCallback, useId } from 'react';
import { DateTime, Info } from 'luxon';
import {
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from '@chakra-ui/react';
import { useObjectMemo } from '../../utils/utils';
import Button from '../Button/Button';
import Box from '../Box/Box';
import Icon from '../Icon/Icon';
import Text from '../Text/Text';
import DatePickerGrid from './DatePickerGrid';
import DatePickerDay, { DatePickerDayContext } from './DatePickerDay';
import DatePickerHeader, {
  DatePickerHeaderContent,
  DatePickerHeaderNext,
  DatePickerHeaderPrevious,
} from './DatePickerHeader';

const daysOfTheWeek = Info.weekdays('short');
const daysCount = 6 * 7;

interface Props {
  controls: 'left' | 'right' | 'both';
  onViewDateChange: (viewDate: DateTime) => void;
  onMonthClick: () => void;
  viewDate: DateTime;
}

const days = createElement(
  Fragment,
  {},
  ...Array.from({ length: daysCount }).map((_, i) => <DatePickerDay n={i + 1} />),
);

export const datePickerMinYear = 1990;
export const datePickerMaxYear = 2100;

const DatePickerMonth = ({ controls, onMonthClick, onViewDateChange, viewDate }: Props) => {
  const onNextMonth = useCallback(() => onViewDateChange(viewDate.plus({ months: 1 })), [onViewDateChange, viewDate]);
  const onPreviousMonth = useCallback(
    () => onViewDateChange(viewDate.minus({ months: 1 })),
    [onViewDateChange, viewDate],
  );

  const dayContext = useObjectMemo({ onNextMonth, onPreviousMonth, viewDate });

  const monthLabelId = useId();
  return (
    <Box data-testid={`controls-${controls}`}>
      <DatePickerHeader controls={controls} onNext={onNextMonth} onPrevious={onPreviousMonth}>
        <DatePickerHeaderPrevious label="previous month" />
        <DatePickerHeaderContent id={monthLabelId}>
          <Button
            color="purple.10"
            flexShrink={0}
            onClick={onMonthClick}
            rightIconName="ChevronDown"
            size="sm"
            variant="tertiary"
          >
            {viewDate.monthLong}
          </Button>
          <NumberInput
            aria-label="year"
            flexShrink={1}
            max={datePickerMaxYear}
            min={datePickerMinYear}
            onChange={(_, year) => onViewDateChange(viewDate.set({ year }))}
            size="small"
            value={viewDate.year}
            w="4.5rem"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper>
                <Icon name="ChevronUp" size="16" />
              </NumberIncrementStepper>
              <NumberDecrementStepper>
                <Icon name="ChevronDown" size="16" />
              </NumberDecrementStepper>
            </NumberInputStepper>
          </NumberInput>
        </DatePickerHeaderContent>
        <DatePickerHeaderNext label="next month" />
      </DatePickerHeader>

      <DatePickerGrid alignItems="center" gridTemplateRows="2rem" justifyItems="center">
        {daysOfTheWeek.map((day) => (
          <Text key={day} color="neutral.50" size="2" textTransform="capitalize">
            {day}
          </Text>
        ))}
      </DatePickerGrid>
      <DatePickerGrid aria-labelledby={monthLabelId} paddingTop="8" role="listbox">
        <DatePickerDayContext value={dayContext}>{days}</DatePickerDayContext>
      </DatePickerGrid>
    </Box>
  );
};

export default DatePickerMonth;
